/** Machine strings stay at the caller boundary: this control never converts a timezone or rounds a loaded value. */
import { useI18n } from '@platform/i18n';
import { CalendarIcon, CheckIcon, ClockIcon } from 'lucide-react';
import { useEffect, useId, useImperativeHandle, useRef, useState, type ComponentProps } from 'react';
import { MiniMonth } from '../calendar/mini-month';
import { addDays, calendarWeek } from '../calendar/model';
import { weekRulesFor } from '../calendar/week-rules';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../components/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover';
import { focusConnected } from '../lib/focus';
import { cn } from '../lib/utils';

export interface DateTimePickerProps extends Omit<ComponentProps<'input'>, 'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'> {
  mode: 'date' | 'time' | 'datetime';
  /** YYYY-MM-DD, HH:mm[:ss], or YYYY-MM-DDTHH:mm[:ss]. Empty means unset. */
  value: string;
  onValueChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  /** Native input step in seconds (date mode retains the native one-day default). Suggestions never round existing values. */
  step?: number | undefined;
  weekStartsOn?: number;
  showWeekNumbers?: boolean;
  /** Explicit region (e.g. en-US/de-DE); otherwise a regionless language can use the user's German timezone. */
  weekLocale?: string;
  weekNumbering?: 'regional' | 'iso';
  /** Regional hint only. The picker never converts dates/times to this zone. Defaults to the browser zone. */
  weekTimeZone?: string;
  /** Calendar callers can retain their stored12/24-hour display preference. */
  formatTime?: (wallTime: Date) => string;
  /** Time-only resource endpoints: explicitly distinguish end-of-day24:00 from start-of-day00:00. */
  allowEndOfDay?: boolean;
}

/** Parse calendar fields locally, avoiding UTC date parsing and browser timezone shifts. */
function calendarDate(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const date = new Date(0);
  date.setFullYear(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setHours(0, 0, 0, 0);
  return date.getFullYear() === Number(match[1]) && date.getMonth() === Number(match[2]) - 1 && date.getDate() === Number(match[3]) ? date : undefined;
}
const dateString = (date: Date) => `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const comparable = (value: string) => value.replace(/(\d{2}:\d{2})(?=$)/, '$1:00').replace(/(\d{2}:\d{2}:\d{2})(?=$)/, '$1.000');
function withinBounds(mode: DateTimePickerProps['mode'], value: string, min?: string, max?: string): boolean {
  const next = comparable(value), lower = min ? comparable(min) : '', upper = max ? comparable(max) : '';
  // Native time inputs allow a range crossing midnight; date/datetime ranges do not wrap.
  return mode === 'time' && lower && upper && lower > upper ? next >= lower || next <= upper : (!lower || next >= lower) && (!upper || next <= upper);
}

export function DateTimePicker({ mode, value, onValueChange, min, max, step, weekStartsOn, showWeekNumbers = true, weekLocale, weekNumbering = 'regional', weekTimeZone, formatTime, allowEndOfDay = false, className, disabled, readOnly, ref, name, ...inputProps }: DateTimePickerProps) {
  const { t, locale, formatDate, formatNumber } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const restoreField = useRef(true);
  useImperativeHandle(ref, () => input.current!);
  const hintId = useId();
  const rules = weekRulesFor(weekLocale ?? locale, { numbering: weekNumbering, timeZone: weekTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, regionLocale: globalThis.navigator?.language ?? locale });
  const weekForRow = (row: Date) => calendarWeek(addDays(row, 3), rules);
  const [open, setOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState('');
  const [pendingTime, setPendingTime] = useState('');
  const [timeQuery, setTimeQuery] = useState('');
  const [followedValue, setFollowedValue] = useState(value);
  if (followedValue !== value) {
    setFollowedValue(value); setPendingDate(''); setPendingTime('');
  }
  const frozen = disabled || readOnly;
  if (frozen && open) setOpen(false);
  const endOfDay = mode === 'time' && allowEndOfDay && value === '24:00';
  const datePart = mode === 'datetime' ? value.split('T')[0] ?? '' : mode === 'date' ? value : '';
  const timePart = mode === 'datetime' ? value.split('T')[1] ?? '' : mode === 'time' ? value : '';
  const date = calendarDate(pendingDate || datePart);
  const lower = mode !== 'time' ? calendarDate(min?.split('T')[0] ?? '') : undefined;
  const upper = mode !== 'time' ? calendarDate(max?.split('T')[0] ?? '') : undefined;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const candidate = date ?? today;
  const reference = lower && candidate < lower ? lower : upper && candidate > upper ? upper : candidate;
  const activeTime = pendingTime || timePart;
  const inBounds = (next: string) => withinBounds(mode, next, min, max);
  useEffect(() => {
    input.current?.setCustomValidity(endOfDay && !withinBounds(mode, value, min, max) ? t('ui.dateTime.outsideBounds') : '');
  }, [endOfDay, mode, value, min, max, t]);
  const commit = (nextDate: string, nextTime: string) => {
    if (frozen) return;
    if (mode === 'datetime') {
      setPendingDate(nextDate); setPendingTime(nextTime);
      if (!nextDate || !nextTime) return;
    }
    const next = mode === 'date' ? nextDate : mode === 'time' ? nextTime : `${nextDate}T${nextTime}`;
    if (!inBounds(next)) return;
    onValueChange(next);
    if (mode === 'date') setOpen(false);
  };
  const setTime = (next: string) => commit(pendingDate || datePart, next);
  const minuteStep = Math.max(1, Math.ceil((step ?? 60) / 60));
  const timeOptions = Array.from({ length: Math.ceil(1440 / minuteStep) }, (_, index) => {
    const minute = index * minuteStep;
    return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
  });
  const currentMinute = activeTime.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(currentMinute) && currentMinute !== '24:00' && !timeOptions.includes(currentMinute)) timeOptions.push(currentMinute);
  const query = timeQuery.trim();
  const parts = /^(\d{1,2})(?::(\d{0,2}))?$/.exec(query);
  const prefix = parts ? `${parts[1]?.padStart(2, '0')}${parts[2] === undefined ? '' : `:${parts[2]}`}` : query;
  const matches = timeOptions.sort().filter((time) => time.startsWith(prefix));
  const selectLabel = t(mode === 'date' ? 'ui.dateTime.chooseDate' : mode === 'time' ? 'ui.dateTime.chooseTime' : 'ui.dateTime.chooseDateTime');
  return <Popover open={open && !frozen} onOpenChange={(next) => {
    if (frozen) return;
    if (next) { restoreField.current = true; setPendingDate(''); setPendingTime(''); setTimeQuery(timePart === '24:00' ? '00' : timePart.slice(0, 2)); }
    setOpen(next);
  }}>
    <div className={cn('flex min-w-0 items-center gap-1', className)} data-slot="date-time-picker">
      <Input {...inputProps} ref={input} lang={locale} type={mode === 'datetime' ? 'datetime-local' : mode} value={endOfDay ? '00:00' : value} min={endOfDay ? undefined : min} max={endOfDay ? undefined : max} step={step} disabled={disabled} readOnly={readOnly}
        aria-describedby={[inputProps['aria-describedby'], endOfDay ? hintId : undefined].filter(Boolean).join(' ') || undefined}
        className="[&::-webkit-calendar-picker-indicator]:hidden"
        onChange={(event) => { if (!frozen) { setPendingDate(''); setPendingTime(''); onValueChange(event.currentTarget.value); } }} />
      {name && <input type="hidden" name={name} value={value} disabled={disabled} />}
      {endOfDay && <span id={hintId} className="shrink-0 text-xs text-muted-foreground">{t('ui.dateTime.endOfDay')}</span>}
      <PopoverTrigger asChild><Button type="button" size="icon" variant="outline" disabled={!!frozen} aria-label={selectLabel}>{mode === 'time' ? <ClockIcon aria-hidden /> : <CalendarIcon aria-hidden />}</Button></PopoverTrigger>
    </div>
    <PopoverContent align="start" aria-label={selectLabel} className="max-h-[calc(100dvh-2rem)] overflow-y-auto" onInteractOutside={() => { restoreField.current = false; }} onCloseAutoFocus={(event) => { if (restoreField.current) focusConnected(event, input); }}>
      {mode !== 'time' && <MiniMonth selected={reference} showSelection={!!date && date.getTime() === reference.getTime()} now={today} weekStartsOn={weekStartsOn ?? rules.weekStartsOn} {...(lower ? { min: lower } : {})} {...(upper ? { max: upper } : {})} disabled={!!frozen}
        {...(showWeekNumbers ? { weekNumbers: {
          heading: t('ui.dateTime.weekHeading'),
          label: (row: Date) => formatNumber(weekForRow(row).week),
          accessibleLabel: (row: Date) => { const week = weekForRow(row); return t('ui.dateTime.weekLabel', { week: formatNumber(week.week), year: formatNumber(week.year, { useGrouping: false }) }); },
        } } : {})}
        onSelect={(day) => commit(dateString(day), pendingTime || timePart)}
        formatMonth={(day) => formatDate(day, { month: 'long', year: 'numeric' })}
        formatWeekdayShort={(day) => formatDate(day, { weekday: 'short' })}
        formatDayNumber={(day) => formatNumber(day.getDate())}
        formatDayLabel={(day) => formatDate(day, { dateStyle: 'full' })}
        labels={{ previousMonth: t('ui.dateTime.previousMonth'), nextMonth: t('ui.dateTime.nextMonth') }} />}
      {mode !== 'date' && <Command shouldFilter={false} className="mt-2 h-auto" label={t('ui.dateTime.searchTime')}>
        <CommandInput className="min-w-0" aria-label={t('ui.dateTime.searchTime')} placeholder={t('ui.dateTime.searchTime')} value={timeQuery} onValueChange={setTimeQuery} disabled={!!frozen} />
        <CommandList className="max-h-40" aria-label={t('ui.dateTime.chooseTime')}>
          <CommandEmpty>{t('table.noResults')}</CommandEmpty>
          {matches.slice(0, 100).map((time) => <CommandItem key={time} value={time} disabled={!!frozen || (mode === 'time' ? !inBounds(time) : !!(pendingDate || datePart) && !inBounds(`${pendingDate || datePart}T${time}`))} onSelect={() => { setTime(time); if (mode === 'time') setOpen(false); }}>
            <CheckIcon aria-hidden className={cn('size-4', time !== currentMinute && 'invisible')} />
            {formatTime ? formatTime(new Date(2000, 0, 1, Number(time.slice(0, 2)), Number(time.slice(3, 5)))) : formatDate(new Date(2000, 0, 1, Number(time.slice(0, 2)), Number(time.slice(3, 5))), { hour: '2-digit', minute: '2-digit' })}
          </CommandItem>)}
        </CommandList>
        {matches.length > 100 && <p className="mt-1 text-xs text-muted-foreground">{t('ui.dateTime.refineTime')}</p>}
      </Command>}
      {mode === 'datetime' && (!pendingDate && !datePart || !pendingTime && !timePart) && <p className="mt-2 text-xs text-muted-foreground" role="status">{t('ui.dateTime.completeSelection')}</p>}
      {mode === 'datetime' && (pendingDate || datePart) && (pendingTime || timePart) && !inBounds(`${pendingDate || datePart}T${pendingTime || timePart}`) && <p className="mt-2 text-xs text-destructive" role="status">{t('ui.dateTime.outsideBounds')}</p>}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {mode === 'time' && allowEndOfDay && <Button type="button" variant="outline" size="sm" disabled={!!frozen || !inBounds('24:00')} onClick={() => { setTime('24:00'); setOpen(false); }}>{t('ui.dateTime.endOfDay')}</Button>}
        {!inputProps.required && <Button type="button" variant="ghost" size="sm" disabled={!!frozen} onClick={() => { onValueChange(''); setOpen(false); }}>{t('ui.dateTime.clear')}</Button>}
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>{t('common.close')}</Button>
      </div>
    </PopoverContent>
  </Popover>;
}
